// System
import { parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Chip, Divider, Stack } from '@mui/material';

// Components
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { FieldRowLabel } from '@Components/shared/labels/FieldRowLabel';
import { handleCopyClick } from '@Components/shared/copyText/CoptyText';
import { RubberDuckLaneStrip } from './RubberDuckLaneStrip';

// Redux
import { useRubberDuckRoundDrawer } from '@Modal/hooks/rounds/useRubberDuckRoundDrawer';

import styles from './SetupRubberDuckRoundDrawerDetails.module.scss';

const statusColor = (
  status?: string
): 'default' | 'success' | 'error' | 'info' | 'warning' | 'primary' => {
  switch (status) {
    case 'WON':
      return 'success';
    case 'CASHED_OUT':
      return 'primary';
    case 'LOST':
      return 'error';
    case 'ONGOING':
      return 'info';
    case 'ABANDONED':
      return 'warning';
    default:
      return 'default';
  }
};

export const SetupRubberDuckRoundDrawerDetails = () => {
  const { selectedRoundData } = useRubberDuckRoundDrawer();
  const { enqueueSnackbar } = useSnackbar();

  const {
    roundId,
    playerId,
    betAmount,
    currency,
    difficulty,
    laneCount,
    failLane,
    reachedLane,
    crossedLanes,
    currentMultiplier,
    totalWinAmount,
    maxWin,
    status,
    autoplay,
    autobetConfig,
    clientHash,
    serverHash,
    roundHash,
    createdAt,
    completedAt,
  } = selectedRoundData;

  const hashes: { label: string; value?: string }[] = [
    { label: 'Client Hash', value: clientHash },
    { label: 'Server Hash', value: serverHash },
    { label: 'Round Hash', value: roundHash },
  ];

  return (
    <Stack
      direction="column"
      spacing={1}
      padding="24px"
      className={styles.SetupRubberDuckRoundDrawerDetails}
    >
      <FieldRowLabel label="Round ID" value={roundId ? roundId : 'No Result'} />
      <FieldRowLabel label="Player ID" value={playerId ? playerId : 'No Result'} />
      <FieldRowLabel
        label="Status"
        value={status ? <Chip size="small" label={status} color={statusColor(status)} /> : 'No Result'}
      />
      <FieldRowLabel label="Difficulty" value={difficulty ? difficulty : 'No Result'} />
      <FieldRowLabel
        label="Bet Amount"
        value={betAmount !== undefined ? `${betAmount} ${currency ?? ''}`.trim() : 'No Result'}
      />
      <FieldRowLabel
        label="Total Win"
        value={
          totalWinAmount !== undefined ? `${totalWinAmount} ${currency ?? ''}`.trim() : 'No Result'
        }
      />
      <FieldRowLabel
        label="Multiplier"
        value={currentMultiplier !== undefined ? `${currentMultiplier}x` : 'No Result'}
      />
      <FieldRowLabel label="Max Win" value={maxWin !== undefined && maxWin !== null ? maxWin : '—'} />
      <FieldRowLabel
        label="Reached Lane"
        value={laneCount ? `${reachedLane ?? 0} / ${laneCount}` : 'No Result'}
      />
      <FieldRowLabel label="Fail Lane" value={failLane !== undefined ? failLane : 'No Result'} />
      <FieldRowLabel label="Autoplay" value={autoplay ? 'True' : 'False'} />
      <FieldRowLabel
        label="Created At"
        value={createdAt ? <DateLabel date={new Date(parseISO(createdAt))} format="long" /> : '—'}
      />
      <FieldRowLabel
        label="Completed At"
        value={completedAt ? <DateLabel date={new Date(parseISO(completedAt))} format="long" /> : '—'}
      />

      <Divider />
      <RubberDuckLaneStrip
        laneCount={laneCount}
        failLane={failLane}
        reachedLane={reachedLane}
        status={status}
        crossedLanes={crossedLanes}
      />

      {autobetConfig && (
        <>
          <Divider />
          <Stack>
            <strong>Autobet Config</strong>
            <div className={styles.AutobetConfigContainer}>
              <p className={styles.AutobetConfigLabel}>Number of Bets: </p>
              <strong className={styles.AutobetConfigValue}>
                {autobetConfig.numberOfBets ?? 0}
              </strong>
            </div>
            <div className={styles.AutobetConfigContainer}>
              <p className={styles.AutobetConfigLabel}>Target Lane: </p>
              <strong className={styles.AutobetConfigValue}>{autobetConfig.targetLane ?? 0}</strong>
            </div>
            <div className={styles.AutobetConfigContainer}>
              <p className={styles.AutobetConfigLabel}>Increase on Win: </p>
              <strong className={styles.AutobetConfigValue}>
                {autobetConfig.increaseOnWin ?? 0}
              </strong>
            </div>
            <div className={styles.AutobetConfigContainer}>
              <p className={styles.AutobetConfigLabel}>Increase on Loss: </p>
              <strong className={styles.AutobetConfigValue}>
                {autobetConfig.increaseOnLoss ?? 0}
              </strong>
            </div>
            <div className={styles.AutobetConfigContainer}>
              <p className={styles.AutobetConfigLabel}>Stop on Profit: </p>
              <strong className={styles.AutobetConfigValue}>
                {autobetConfig.stopOnProfit ?? 0}
              </strong>
            </div>
            <div className={styles.AutobetConfigContainer}>
              <p className={styles.AutobetConfigLabel}>Stop on Loss: </p>
              <strong className={styles.AutobetConfigValue}>{autobetConfig.stopOnLoss ?? 0}</strong>
            </div>
          </Stack>
        </>
      )}

      <Divider />
      <Stack>
        <strong>Provably Fair</strong>
        {hashes.map(({ label, value }) => (
          <div className={styles.HashRow} key={label}>
            <p className={styles.HashLabel}>{label}: </p>
            <p className={styles.HashValue}>{value || 'No Result'}</p>
            {value && (
              <ContentCopyIcon
                fontSize="small"
                sx={{ cursor: 'pointer' }}
                onClick={(event) => handleCopyClick(event, value, enqueueSnackbar)}
              />
            )}
          </div>
        ))}
        <div className={styles.HashRow}>
          <p className={styles.HashLabel}>Fail Lane: </p>
          <p className={styles.HashValue}>{failLane !== undefined ? failLane : 'No Result'}</p>
        </div>
      </Stack>
    </Stack>
  );
};
